// Review queue voor EmberConcepts. PRD-04 §3.4.

import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { CounselorLayout } from "@/components/CounselorLayout";

interface Concept {
  id: string;
  clientId: string;
  clientName: string;
  subject: string;
  mode: "BESCHERMEND" | "DIPLOMATIEK" | "CRISIS" | "VRIENDELIJK" | "VERTALER";
  channel: "EMAIL" | "LETTER" | "SMS" | "IN_APP";
  threatLevel: "NONE" | "LOW" | "MEDIUM" | "HIGH" | "CRITICAL";
  recipientEmail: string | null;
  recipientName: string | null;
  createdAt: string;
}

const THREAT_BADGE: Record<string, string> = {
  CRITICAL: "bg-red-100 text-red-900",
  HIGH: "bg-orange-100 text-orange-900",
  MEDIUM: "bg-amber-100 text-amber-900",
  LOW: "bg-blue-100 text-blue-900",
  NONE: "bg-neutral-100 text-neutral-700",
};

export function ReviewQueuePage() {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const [feedback, setFeedback] = useState<Record<string, string>>({});
  const [selected, setSelected] = useState<Set<string>>(new Set());

  const query = useQuery({
    queryKey: ["review-queue"],
    queryFn: () => api.get<{ concepts: Concept[] }>("/v1/counselor/review-queue"),
    refetchInterval: 30_000,
  });

  const review = useMutation({
    mutationFn: ({ id, action, fb }: { id: string; action: "APPROVE" | "REJECT"; fb?: string }) =>
      api.put(`/v1/counselor/concepts/${id}/review`, { action, feedback: fb }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["review-queue"] }),
  });

  const bulkApprove = useMutation({
    mutationFn: (ids: string[]) =>
      api.post<{ approved: number; skipped: number }>(
        "/v1/counselor/concepts/bulk-approve",
        { conceptIds: ids }
      ),
    onSuccess: () => {
      setSelected(new Set());
      qc.invalidateQueries({ queryKey: ["review-queue"] });
    },
  });

  const concepts = query.data?.concepts ?? [];
  const bulkEligible = concepts.filter((c) =>
    ["NONE", "LOW", "MEDIUM"].includes(c.threatLevel)
  );

  function toggle(id: string) {
    setSelected((s) => {
      const n = new Set(s);
      if (n.has(id)) n.delete(id);
      else if (n.size < 25) n.add(id);
      return n;
    });
  }

  function selectAllEligible() {
    setSelected(new Set(bulkEligible.slice(0, 25).map((c) => c.id)));
  }

  return (
    <CounselorLayout title={t("counselor.review.title", "Review queue")}>
      <p className="text-sm text-neutral-600 mb-4">
        {t(
          "counselor.review.intro",
          "Ember-concepten met HIGH/CRITICAL dreigingsniveau wachten op review voordat ze verstuurd worden (ADR-004)."
        )}
      </p>

      {bulkEligible.length > 0 && (
        <div className="bg-blue-50 border border-blue-200 rounded-xl p-3 mb-4 flex items-center gap-3 text-sm">
          <span className="font-medium text-blue-900">
            {t("counselor.review.bulkLabel", "Bulk-approve LOW/MEDIUM (max 25):")}
          </span>
          <span className="text-blue-700">
            {selected.size} {t("counselor.review.bulkSelected", "geselecteerd")}
          </span>
          <div className="ml-auto flex gap-2">
            <button
              onClick={selectAllEligible}
              className="px-2 py-1 text-xs bg-white border border-blue-300 hover:bg-blue-100 rounded text-blue-800"
            >
              {t("counselor.review.bulkSelectAll", "Selecteer alle")} ({bulkEligible.length})
            </button>
            <button
              onClick={() => bulkApprove.mutate(Array.from(selected))}
              disabled={selected.size === 0 || bulkApprove.isPending}
              className="px-3 py-1 text-xs bg-green-600 hover:bg-green-700 disabled:opacity-50 text-white rounded font-medium"
            >
              {t("counselor.review.bulkApprove", "Bulk goedkeuren")} ({selected.size})
            </button>
          </div>
        </div>
      )}

      {query.isLoading ? (
        <p className="text-sm text-neutral-500">{t("loading", "Laden…")}</p>
      ) : concepts.length === 0 ? (
        <div className="bg-white border border-neutral-200 rounded-xl p-8 text-center">
          <p className="text-3xl mb-2">✓</p>
          <p className="text-sm text-neutral-700 font-medium">
            {t("counselor.review.empty", "Geen openstaande concepten")}
          </p>
          <p className="text-xs text-neutral-500 mt-1">
            {t(
              "counselor.review.emptyHelp",
              "Concepten verschijnen hier zodra Ember een HIGH/CRITICAL bericht detecteert (v0.2)."
            )}
          </p>
        </div>
      ) : (
        <div className="space-y-3">
          {concepts.map((c) => (
            <article key={c.id} className="bg-white border border-neutral-200 rounded-xl p-5">
              <header className="flex items-start justify-between gap-3 mb-3">
                <div className="flex items-start gap-3">
                  {["NONE", "LOW", "MEDIUM"].includes(c.threatLevel) && (
                    <input
                      type="checkbox"
                      checked={selected.has(c.id)}
                      onChange={() => toggle(c.id)}
                      disabled={!selected.has(c.id) && selected.size >= 25}
                      className="mt-1 h-4 w-4"
                      aria-label={t("counselor.review.selectForBulk", "Selecteer voor bulk-approve")}
                    />
                  )}
                  <div>
                  <div className="flex items-center gap-2 mb-1">
                    <span className={`text-xs px-2 py-0.5 rounded ${THREAT_BADGE[c.threatLevel]}`}>
                      {c.threatLevel}
                    </span>
                    <span className="text-xs px-2 py-0.5 rounded bg-neutral-100 text-neutral-700">
                      {c.mode}
                    </span>
                    <span className="text-xs px-2 py-0.5 rounded bg-neutral-100 text-neutral-700">
                      {c.channel}
                    </span>
                  </div>
                  <h3 className="font-semibold">{c.subject}</h3>
                  <p className="text-xs text-neutral-600 mt-0.5">
                    {t("counselor.review.client", "Client")}: <strong>{c.clientName}</strong> ·{" "}
                    {t("counselor.review.to", "Naar")}: {c.recipientName ?? c.recipientEmail}
                  </p>
                  </div>
                </div>
                <time className="text-xs text-neutral-400">
                  {new Date(c.createdAt).toLocaleString("nl-NL", {
                    dateStyle: "short",
                    timeStyle: "short",
                  })}
                </time>
              </header>

              <textarea
                placeholder={t("counselor.review.feedback", "Feedback (optioneel, verplicht bij afwijzen)")}
                value={feedback[c.id] ?? ""}
                onChange={(e) => setFeedback((f) => ({ ...f, [c.id]: e.target.value }))}
                rows={2}
                className="w-full px-3 py-2 border border-neutral-300 rounded-lg text-sm mb-3"
              />

              <div className="flex gap-2">
                <button
                  onClick={() => review.mutate({ id: c.id, action: "APPROVE", fb: feedback[c.id] })}
                  disabled={review.isPending}
                  className="px-3 py-1.5 bg-green-600 hover:bg-green-700 disabled:opacity-60 text-white text-sm font-medium rounded-lg"
                >
                  {t("counselor.review.approve", "Goedkeuren")}
                </button>
                <button
                  onClick={() => {
                    if (!feedback[c.id]?.trim()) {
                      alert(t("counselor.review.rejectNeedsFeedback", "Feedback verplicht bij afwijzen"));
                      return;
                    }
                    review.mutate({ id: c.id, action: "REJECT", fb: feedback[c.id] });
                  }}
                  disabled={review.isPending}
                  className="px-3 py-1.5 bg-red-600 hover:bg-red-700 disabled:opacity-60 text-white text-sm font-medium rounded-lg"
                >
                  {t("counselor.review.reject", "Afwijzen")}
                </button>
              </div>
            </article>
          ))}
        </div>
      )}
    </CounselorLayout>
  );
}
